// src/utils/towerSiteRAPDetailExport.js
// Export Tower Site RAP Detail to PDF and Excel - Only used items with RAP values
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

/**
 * Format currency for display
 */
const formatCurrency = (value) => {
  if (!value && value !== 0) return '-';
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

/**
 * Format volume
 */
const formatVolume = (value) => {
  if (!value && value !== 0) return '0';
  return new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4
  }).format(value);
};

/**
 * Collect RAP items grouped by category
 * Filter: Only items with isNotUsed = false
 * Include: Both BOQ items and NEW items
 */
const collectRAPItemsByCategory = (rapData, categories) => {
  const groupedItems = [];
  
  categories.forEach(category => {
    const categoryData = rapData[category.id];
    if (!categoryData || !categoryData.items) return;
    
    // Filter: Only USED items (isNotUsed = false)
    const usedItems = categoryData.items.filter(item => !item.isNotUsed);
    
    if (usedItems.length > 0) {
      groupedItems.push({
        category: {
          id: category.id,
          code: category.categoryCode,
          name: category.categoryName
        },
        items: usedItems.map(item => ({
          itemDescription: item.itemDescription,
          unit: item.unit,
          volume: item.volume,
          rapUnitPrice: item.rapUnitPrice || 0,
          rapTotalPrice: item.rapTotalPrice || 0,
          remarks: item.remarks || '',
          sourceType: item.sourceType // BOQ or NEW
        })),
        subtotal: usedItems.reduce((sum, item) => sum + (parseFloat(item.rapTotalPrice) || 0), 0)
      });
    }
  });
  
  return groupedItems;
};

/**
 * Export Tower Site RAP Detail to PDF
 * 
 * @param {object} tower - Tower site object
 * @param {object} rapData - RAP data (categoryId -> items)
 * @param {array} categories - Budget categories
 * @param {number} priceCoefficient - Price coefficient
 */
export const exportTowerSiteRAPDetailToPDF = (tower, rapData, categories, priceCoefficient) => {
  const doc = new jsPDF('landscape');
  
  // Title
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text('RAP DETAIL - TOWER SITAC', 14, 15);
  
  // Tower Info
  doc.setFontSize(12);
  doc.setFont(undefined, 'normal');
  doc.text(`Site ID: ${tower.siteId}`, 14, 23);
  
  doc.setFontSize(10);
  doc.text(`Province: ${tower.provinsi || '-'} | Coefficient: ×${priceCoefficient.toFixed(4)}`, 14, 30);
  doc.text(`Site Name: ${tower.siteName || '-'}`, 14, 36);
  doc.text(`Tower Height: ${tower.towerHeight ? `${tower.towerHeight} m` : '-'} | Type: ${tower.typeSiteContract || 'Tower'}`, 14, 42);
  doc.text(`Generated: ${new Date().toLocaleDateString('id-ID')}`, 14, 48);
  
  // Collect items grouped by category
  const groupedItems = collectRAPItemsByCategory(rapData, categories);
  
  if (groupedItems.length === 0) {
    doc.setFontSize(12);
    doc.text('No items to export', 14, 60);
    const filename = `Tower_SITAC_RAP_Detail_${tower.siteId}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
    return;
  }
  
  let currentY = 54;
  let itemNo = 1;
  let grandTotal = 0;
  
  // Process each category
  groupedItems.forEach((group, groupIndex) => {
    // Check if we need a new page
    if (currentY > 180) {
      doc.addPage();
      currentY = 15;
    }
    
    // Category Header
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setFillColor(41, 128, 185);
    doc.rect(14, currentY, 267, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.text(`${group.category.code}. ${group.category.name}`, 16, currentY + 5.5);
    doc.setTextColor(0, 0, 0);
    currentY += 10;
    
    // Prepare table data for this category
    const tableData = group.items.map(item => {
      const row = [
        itemNo++,
        item.itemDescription,
        item.unit,
        formatVolume(item.volume),
        formatCurrency(item.rapUnitPrice),
        formatCurrency(item.rapTotalPrice),
        item.remarks || '-'
      ];
      return row;
    });
    
    // Add category table
    doc.autoTable({
      startY: currentY,
      head: [['NO', 'DESCRIPTION', 'UNIT', 'VOLUME', 'RAP UNIT PRICE', 'RAP TOTAL PRICE', 'REMARKS']],
      body: tableData,
      theme: 'grid',
      styles: { 
        fontSize: 8, 
        cellPadding: 2 
      },
      headStyles: {
        fillColor: [52, 152, 219],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { cellWidth: 12, halign: 'center' },
        1: { cellWidth: 90 },
        2: { cellWidth: 20, halign: 'center' },
        3: { cellWidth: 25, halign: 'right' },
        4: { cellWidth: 35, halign: 'right' },
        5: { cellWidth: 40, halign: 'right' },
        6: { cellWidth: 45 }
      },
      didDrawPage: (data) => {
        currentY = data.cursor.y;
      }
    });
    
    currentY = doc.lastAutoTable.finalY + 2;
    
    // Category Subtotal
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text(`Subtotal ${group.category.code}:`, 190, currentY);
    doc.text(formatCurrency(group.subtotal), 240, currentY, { align: 'right' });
    
    grandTotal += group.subtotal;
    currentY += 8;
  });
  
  // Grand Total
  if (currentY > 250) {
    doc.addPage();
    currentY = 15;
  }
  
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.setFillColor(255, 235, 59);
  doc.rect(14, currentY, 267, 10, 'F');
  doc.text('GRAND TOTAL:', 150, currentY + 7);
  doc.text(formatCurrency(grandTotal), 270, currentY + 7, { align: 'right' });
  
  // Footer with page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }
  
  // Save
  const filename = `Tower_SITAC_RAP_Detail_${tower.siteId}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
};

/**
 * Export Tower Site RAP Detail to Excel
 * 
 * @param {object} tower - Tower site object
 * @param {object} rapData - RAP data (categoryId -> items)
 * @param {array} categories - Budget categories
 * @param {number} priceCoefficient - Price coefficient
 */
export const exportTowerSiteRAPDetailToExcel = (tower, rapData, categories, priceCoefficient) => {
  // Collect items grouped by category
  const groupedItems = collectRAPItemsByCategory(rapData, categories);
  
  if (groupedItems.length === 0) {
    alert('No items to export');
    return;
  }
  
  const excelData = [];
  
  // Header
  excelData.push(['RAP DETAIL - TOWER SITAC']);
  excelData.push([`Site ID: ${tower.siteId}`]);
  excelData.push([`Province: ${tower.provinsi || '-'} | Coefficient: ×${priceCoefficient.toFixed(4)}`]);
  excelData.push([`Site Name: ${tower.siteName || '-'}`]);
  excelData.push([`Tower Height: ${tower.towerHeight ? `${tower.towerHeight} m` : '-'} | Type: ${tower.typeSiteContract || 'Tower'}`]);
  excelData.push([`Generated: ${new Date().toLocaleDateString('id-ID')}`]);
  excelData.push([]);
  
  let itemNo = 1;
  let grandTotal = 0;
  
  // Process each category
  groupedItems.forEach((group, groupIndex) => {
    // Category Header
    excelData.push([`${group.category.code}. ${group.category.name}`]);
    
    // Column headers for this category
    excelData.push(['NO', 'DESCRIPTION', 'UNIT', 'VOLUME', 'RAP UNIT PRICE', 'RAP TOTAL PRICE', 'REMARKS']);
    
    // Data rows
    group.items.forEach(item => {
      excelData.push([
        itemNo++,
        item.itemDescription,
        item.unit,
        parseFloat(item.volume) || 0,
        parseFloat(item.rapUnitPrice) || 0,
        parseFloat(item.rapTotalPrice) || 0,
        item.remarks || ''
      ]);
    });
    
    // Category Subtotal
    excelData.push(['', '', '', '', `Subtotal ${group.category.code}:`, group.subtotal, '']);
    excelData.push([]);
    
    grandTotal += group.subtotal;
  });
  
  // Grand Total
  excelData.push(['', '', '', '', 'GRAND TOTAL:', grandTotal, '']);
  
  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(excelData);
  
  // Set column widths
  ws['!cols'] = [
    { wch: 5 },   // NO
    { wch: 60 },  // DESCRIPTION
    { wch: 10 },  // UNIT
    { wch: 12 },  // VOLUME
    { wch: 20 },  // RAP UNIT PRICE
    { wch: 20 },  // RAP TOTAL PRICE
    { wch: 30 }   // REMARKS
  ];
  
  // Create workbook
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'RAP Detail');
  
  // Save
  const filename = `Tower_SITAC_RAP_Detail_${tower.siteId}_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(wb, filename);
};